import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import AffiliateButton from '@/components/AffiliateButton';
import Rating from '@/components/Rating';
import ProductCard from '@/components/ProductCard';
import Spinner from '@/components/Spinner';
import { API, formatPrice } from '@/lib/utils';
import { useSEO } from '@/lib/useSEO';
import { ArrowLeft, Check, X, SealCheck, Warning } from '@phosphor-icons/react';

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useSEO({
    title: product ? `${product.name} — review & prijs` : null,
    description: product ? `${product.short_description} ${product.is_best_buy ? 'Beste keuze 2026. ' : ''}Vergelijk prijzen bij Bol.com, Amazon en Coolblue.` : null,
    image: product?.image,
    path: `/product/${slug}`,
    type: 'product',
    jsonLd: product ? {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      description: product.long_description,
      image: product.image,
      brand: { '@type': 'Brand', name: product.brand },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: product.rating,
        reviewCount: product.review_count,
      },
      offers: {
        '@type': 'Offer',
        priceCurrency: 'EUR',
        price: product.price,
        availability: 'https://schema.org/InStock',
        priceValidUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
        url: `https://noodpakket.nl/product/${slug}`,
      },
    } : null,
  });

  useEffect(() => {
    setProduct(null); setNotFound(false);
    axios.get(`${API}/products/${slug}`)
      .then((r) => {
        setProduct(r.data);
        axios.get(`${API}/products?category=${r.data.category}&limit=5`).then((rel) => {
          setRelated(rel.data.filter((p) => p.slug !== r.data.slug).slice(0, 4));
        });
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="container py-24 text-center">
      <h1 className="font-display font-black text-3xl mb-4">Product niet gevonden</h1>
      <Link to="/" className="text-primary font-semibold">Terug naar home</Link>
    </div>
  );
  if (!product) return <Spinner />;

  return (
    <div data-testid={`page-product-${slug}`}>
      <section className="border-b border-border bg-card">
        <div className="container py-10 lg:py-14 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-6">
            <Link to="/categorieen" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-5">
              <ArrowLeft size={16} /> Terug
            </Link>
            <div className="relative border border-border rounded-sm overflow-hidden bg-muted aspect-[4/3]">
              <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
              {product.is_best_buy && (
                <div className="absolute top-4 left-4 bg-accent text-accent-foreground px-3 py-1.5 text-xs font-bold uppercase tracking-widest rounded-sm flex items-center gap-1">
                  <SealCheck weight="fill" size={14} /> Beste keuze 2026
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="overline text-muted-foreground mb-2">{product.brand}</div>
            <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight">{product.name}</h1>
            <div className="mt-5">
              <Rating rating={product.rating} count={product.review_count} />
            </div>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed">{product.long_description}</p>

            <div className="mt-8 bg-background border border-border p-6 rounded-sm">
              <div className="overline text-muted-foreground mb-1">Vanaf</div>
              <div className="font-display font-black text-4xl mb-5">{formatPrice(product.price)}</div>
              <div className="flex flex-col sm:flex-row flex-wrap gap-3">
                {(product.affiliates || []).map((a) => (
                  <AffiliateButton key={a.shop} shop={a.shop} url={a.url} size="lg" testId={`affiliate-${a.shop}-${product.slug}`} />
                ))}
              </div>
              <div className="mt-5 flex items-start gap-2 text-xs text-muted-foreground">
                <Warning size={14} weight="bold" className="text-accent mt-0.5 flex-shrink-0" />
                <span>Deze links zijn affiliate-links. We ontvangen een kleine commissie bij aankoop — voor jou verandert de prijs niet.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pros & Cons */}
      <section className="container py-16">
        <div className="grid md:grid-cols-2 gap-5">
          <div className="border border-border bg-card p-8 rounded-sm">
            <div className="overline text-primary mb-4">Pluspunten</div>
            <ul className="space-y-3">
              {(product.pros || []).map((p, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check weight="bold" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-base">{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="border border-border bg-card p-8 rounded-sm">
            <div className="overline text-accent mb-4">Aandachtspunten</div>
            <ul className="space-y-3">
              {(product.cons || []).map((c, i) => (
                <li key={i} className="flex items-start gap-3">
                  <X weight="bold" size={20} className="text-accent flex-shrink-0 mt-0.5" />
                  <span className="text-base">{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-muted/50 border-t border-border">
          <div className="container py-16 lg:py-20">
            <h2 className="font-display font-black text-3xl mb-10">Vergelijkbare producten</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {(related || []).map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
